"use client";

import React, { useEffect, useState } from "react";
import { Menu, X } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from "@/components/ui/navigation-menu";
import { MenuProvider, useMenu } from "@/integrations/wordpress/WordPressMenuProvider";
function DesktopNav({
  activeHref,
  setActiveHref
}: {
  activeHref: string;
  setActiveHref: (v: string) => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (loading) {
    return <div data-section-id="223278" className="hidden md:flex gap-4">
        {[1, 2, 3].map((_, i) => <div key={i} data-index={i} className="h-8 w-20 bg-muted/50 animate-pulse rounded-none" />)}
      </div>;
  }
  return <NavigationMenu className="hidden md:flex">
      <NavigationMenuList className="gap-2">
        {menuItems.map((item, i) => {
        const itemHref = item.href || "#";
        const isActive = activeHref === itemHref || activeHref === "/" && itemHref === "/";
        return <NavigationMenuItem key={item.id} data-index={i}>
              <NavigationMenuLink asChild>
                <Link to={itemHref} onClick={() => setActiveHref(itemHref)} className={`group relative px-4 py-2 text-[22px] font-default font-bold uppercase tracking-tight transition-colors outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-none ${isActive ? "text-primary" : "text-foreground hover:text-primary"}`}>
                  {item.label}
                  <span className={`absolute inset-x-4 bottom-1 h-[1px] bg-primary transition-transform duration-300 ease-out ${isActive ? "scale-x-100" : "scale-x-0 group-hover:scale-x-100"}`} />
                </Link>
              </NavigationMenuLink>
            </NavigationMenuItem>;
      })}
      </NavigationMenuList>
    </NavigationMenu>;
}
function MobileNav({
  isOpen,
  setIsOpen,
  activeHref,
  setActiveHref
}: {
  isOpen: boolean;
  setIsOpen: (v: boolean) => void;
  activeHref: string;
  setActiveHref: (v: string) => void;
}) {
  const {
    menuItems,
    loading
  } = useMenu();
  if (!isOpen) return null;
  return <div className="absolute top-full left-0 w-full bg-background/95 backdrop-blur-xl border-b border-primary/20 shadow-2xl md:hidden flex flex-col py-4 px-4 gap-2 origin-top animate-in slide-in-from-top-2 fade-in duration-200">
      {loading ? <div className="flex flex-col gap-2">
          {[1, 2, 3].map((_, i) => <div key={i} data-index={i} className="h-10 w-full bg-muted/50 animate-pulse rounded-none" />)}
        </div> : menuItems.map((item, i) => {
      const itemHref = item.href || "#";
      const isActive = activeHref === itemHref || activeHref === "/" && itemHref === "/";
      return <Link key={item.id} to={itemHref} className={`p-3 text-[22px] font-default font-bold uppercase tracking-tight rounded-none transition-colors ${isActive ? "text-primary bg-accent/10" : "text-foreground hover:text-primary hover:bg-accent/10"}`} onClick={() => {
        setActiveHref(itemHref);
        setIsOpen(false);
      }} data-index={i}>
              {item.label}
            </Link>;
    })}
      <div className="pt-4 mt-2 border-t border-primary/20">
        <Button className="w-full bg-gradient-to-r from-primary to-primary/80 text-primary-foreground border-0 shadow-lg shadow-primary/10 hover:opacity-90 transition-opacity rounded-none h-12 text-base font-default font-bold uppercase tracking-tight">
          Inscrire une équipe
        </Button>
      </div>
    </div>;
}
export default function Header() {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileOpen, setIsMobileOpen] = useState(false);
  const [activeHref, setActiveHref] = useState<string>("");
  useEffect(() => {
    setActiveHref(window.location.pathname);
  }, []);
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener("scroll", handleScroll, {
      passive: true
    });
    return () => window.removeEventListener("scroll", handleScroll);
  }, []);
  useEffect(() => {
    const handleAnchorClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      const anchor = target.closest("a[href]") as HTMLAnchorElement | null;
      if (anchor) {
        const href = anchor.getAttribute("href");
        if (!href) return;
        if (href === "/#") {
          e.preventDefault();
          window.scrollTo({
            top: 0,
            behavior: "smooth"
          });
          setActiveHref(href);
          return;
        }
        let hash = "";
        if (href.startsWith("#")) {
          hash = href;
        } else if (href.startsWith("/") && href.includes("#")) {
          const [path, hashPart] = href.split("#");
          if (hashPart === "") {
            e.preventDefault();
            window.scrollTo({
              top: 0,
              behavior: "smooth"
            });
            setActiveHref(href);
            return;
          }
          if (path === "/" || path === window.location.pathname) {
            hash = "#" + hashPart;
          }
        }
        if (hash && hash !== "#") {
          const element = document.querySelector(hash);
          if (element) {
            e.preventDefault();
            element.scrollIntoView({
              behavior: "smooth",
              block: "start"
            });
            setIsMobileOpen(false);
            setActiveHref(href);
          }
        }
      }
    };
    document.addEventListener("click", handleAnchorClick);
    return () => document.removeEventListener("click", handleAnchorClick);
  }, []);
  return <section id="kickoff-events" className={`sticky top-0 z-50 w-full transition-all duration-300 bg-background text-foreground ${isScrolled ? "bg-background/80 backdrop-blur-md border-b border-primary/30 shadow-md" : "border-b border-transparent"}`}>
      <div className="container mx-auto px-4 md:px-6 h-24 flex items-center justify-between">
        
        <div className="flex-shrink-0 flex items-center">
          <Link to="/" onClick={() => setActiveHref("/")} className="flex items-center gap-2 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary rounded-none">
            <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1647492/img/image_whnn072y.png" className="w-[122px] h-[80px] my-[5px]" alt="Kickoff Events" />
          </Link>
        </div>

        
        <div className="hidden md:flex flex-1 justify-center">
          <MenuProvider menu_id="46">
            <DesktopNav activeHref={activeHref} setActiveHref={setActiveHref} />
          </MenuProvider>
        </div>

        
        <div className="flex items-center justify-end gap-4 flex-shrink-0">
          <Button className="hidden md:inline-flex bg-gradient-to-br from-primary to-primary/70 text-primary-foreground border-0 shadow-lg shadow-primary/10 hover:opacity-90 transition-opacity rounded-none px-6 font-default font-bold uppercase tracking-tight">
            Inscrire une équipe
          </Button>

          <Button variant="ghost" size="icon" className="md:hidden text-foreground hover:text-primary focus-visible:ring-2 focus-visible:ring-primary" onClick={() => setIsMobileOpen(!isMobileOpen)} aria-label={isMobileOpen ? "Close menu" : "Open menu"}>
            {isMobileOpen ? <X className="size-6" /> : <Menu className="size-6" />}
          </Button>
        </div>
      </div>

      
      <MenuProvider menu_id="46">
        <MobileNav isOpen={isMobileOpen} setIsOpen={setIsMobileOpen} activeHref={activeHref} setActiveHref={setActiveHref} />
      </MenuProvider>
    </section>;
}